import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Ban, Building2, Search, Trash2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const PLAN_BADGE = {
  free: "bg-slate-800 text-slate-300",
  pro: "bg-amber-500/15 text-amber-300",
  enterprise: "bg-emerald-500/15 text-emerald-300",
};

export default function AdminTenants() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [plan, setPlan] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (plan) params.set("plan", plan);
      if (includeDeleted) params.set("include_deleted", "true");
      params.set("page_size", "100");
      const { data } = await api.get(`/platform/tenants?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setSelected(new Set());
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load tenants");
    } finally {
      setLoading(false);
    }
  }, [search, plan, includeDeleted]);

  useEffect(() => {
    const h = setTimeout(load, 250);
    return () => clearTimeout(h);
  }, [load]);

  function toggle(id) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected((s) => {
      if (s.size === items.length && items.length > 0) return new Set();
      return new Set(items.map((t) => t.id));
    });
  }

  async function bulk(action) {
    if (selected.size === 0) return;
    if (action === "archive" && !window.confirm(`Archive (soft-delete) ${selected.size} tenant(s)? Users will be deactivated.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/platform/tenants/bulk-action", {
        ids: Array.from(selected),
        action,
      });
      const ok = data.results.filter((r) => r.ok).length;
      const fail = data.results.length - ok;
      toast.success(`${action}: ${ok} ok${fail ? `, ${fail} failed` : ""}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk action failed");
    } finally {
      setBusy(false);
    }
  }

  const allSelected = items.length > 0 && selected.size === items.length;

  return (
    <div className="space-y-6" data-testid="admin-tenants-page">
      <header className="flex flex-col gap-2">
        <h1 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
          Tenants
        </h1>
        <p className="text-sm text-slate-400">
          {total} tenant{total === 1 ? "" : "s"} on the platform.
        </p>
      </header>

      <div className="flex flex-col gap-3 rounded-2xl border border-slate-800 bg-slate-900/70 p-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name…"
            data-testid="admin-tenants-search"
            className="w-full rounded-lg border border-slate-700 bg-slate-950 py-2 ps-9 pe-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-500 focus:outline-none"
          />
        </div>
        <select
          value={plan}
          onChange={(e) => setPlan(e.target.value)}
          data-testid="admin-tenants-plan-filter"
          className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:border-amber-500 focus:outline-none"
        >
          <option value="">All plans</option>
          <option value="free">Free</option>
          <option value="pro">Pro</option>
          <option value="enterprise">Enterprise</option>
        </select>
        <label className="inline-flex items-center gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            checked={includeDeleted}
            onChange={(e) => setIncludeDeleted(e.target.checked)}
            data-testid="admin-tenants-include-deleted"
            className="accent-amber-500"
          />
          Include deleted
        </label>
      </div>

      {error && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">
          {typeof error === "string" ? error : JSON.stringify(error)}
        </div>
      )}

      {selected.size > 0 && (
        <div
          className="sticky top-4 z-30 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 backdrop-blur"
          data-testid="admin-tenants-bulk-bar"
        >
          <div className="text-sm text-amber-100">
            <strong data-testid="admin-tenants-selected-count">{selected.size}</strong> tenant(s) selected
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              disabled={busy}
              onClick={() => bulk("suspend")}
              data-testid="admin-tenants-bulk-suspend"
              className="inline-flex items-center gap-1 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-xs text-red-200 hover:bg-red-500/20"
            >
              <Ban className="h-3.5 w-3.5" /> Suspend selected
            </button>
            <button
              disabled={busy}
              onClick={() => bulk("activate")}
              data-testid="admin-tenants-bulk-activate"
              className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs text-emerald-200 hover:bg-emerald-500/20"
            >
              <Undo2 className="h-3.5 w-3.5" /> Activate selected
            </button>
            <button
              disabled={busy}
              onClick={() => bulk("archive")}
              data-testid="admin-tenants-bulk-archive"
              className="inline-flex items-center gap-1 rounded-md border border-red-500/40 bg-transparent px-3 py-1.5 text-xs text-red-300 hover:bg-red-500/10"
            >
              <Trash2 className="h-3.5 w-3.5" /> Archive selected
            </button>
            <button
              onClick={() => setSelected(new Set())}
              data-testid="admin-tenants-bulk-clear"
              className="rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <div className="mobile-cards overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/70">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="w-10 px-3 py-3 text-start">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  data-testid="admin-tenants-select-all"
                  className="accent-amber-500"
                />
              </th>
              <th className="px-4 py-3 text-start">Tenant</th>
              <th className="px-4 py-3 text-start">Plan</th>
              <th className="px-4 py-3 text-start">Plan dates</th>
              <th className="px-4 py-3 text-start">Status</th>
              <th className="px-4 py-3 text-end">Users</th>
              <th className="px-4 py-3 text-end">Invoices</th>
              <th className="px-4 py-3 text-start">Admin</th>
            </tr>
          </thead>
          <tbody data-testid="admin-tenants-table">
            {loading && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-slate-500">
                  Loading tenants…
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-slate-500">
                  No tenants match the filters.
                </td>
              </tr>
            )}
            {!loading &&
              items.map((t) => {
                const isSel = selected.has(t.id);
                const daysToRenewal = t.plan_ends_at
                  ? Math.round((new Date(t.plan_ends_at) - Date.now()) / 86400000)
                  : null;
                return (
                <tr
                  key={t.id}
                  className={`border-t border-slate-800 transition-colors hover:bg-slate-900 ${isSel ? "bg-amber-500/5" : ""}`}
                  data-testid={`admin-tenant-row-${t.id}`}
                >
                  <td className="px-3 py-3" data-label="">
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggle(t.id)}
                      data-testid={`admin-tenant-check-${t.id}`}
                      className="accent-amber-500"
                    />
                  </td>
                  <td className="px-4 py-3" data-label="Tenant">
                    <Link
                      to={`/admin/tenants/${t.id}`}
                      className="inline-flex items-center gap-2 font-medium text-white hover:text-amber-300"
                    >
                      <Building2 className="h-4 w-4 text-slate-500" />
                      <span className="truncate">{t.name}</span>
                      {t.is_deleted && (
                        <span className="ms-2 rounded-md bg-slate-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-400">
                          archived
                        </span>
                      )}
                    </Link>
                  </td>
                  <td className="px-4 py-3" data-label="Plan">
                    <span
                      className={`rounded-md px-2 py-0.5 text-xs uppercase tracking-wide ${PLAN_BADGE[t.subscription_plan] || PLAN_BADGE.free}`}
                    >
                      {t.subscription_plan}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs" data-label="Plan dates">
                    {t.plan_started_at ? (
                      <div className="text-slate-300">Started {new Date(t.plan_started_at).toISOString().slice(0, 10)}</div>
                    ) : <span className="text-slate-500">—</span>}
                    {t.plan_ends_at ? (
                      <div className={daysToRenewal < 0 ? "text-red-400" : daysToRenewal < 7 ? "text-amber-400" : "text-slate-500"}>
                        {daysToRenewal < 0
                          ? `Expired ${Math.abs(daysToRenewal)}d ago`
                          : `Renews in ${daysToRenewal}d`}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3" data-label="Status">
                    <span
                      className={
                        t.is_active
                          ? "inline-flex items-center gap-1.5 text-xs text-emerald-300"
                          : "inline-flex items-center gap-1.5 text-xs text-red-300"
                      }
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${t.is_active ? "bg-emerald-400" : "bg-red-400"}`}
                      />
                      {t.is_active ? "Active" : "Suspended"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-end tabular-nums text-slate-200" data-label="Users">
                    {t.user_count}
                  </td>
                  <td className="px-4 py-3 text-end tabular-nums text-slate-200" data-label="Invoices">
                    {t.invoice_count}
                  </td>
                  <td className="px-4 py-3 text-slate-300" data-label="Admin">
                    {t.admin_email || "—"}
                  </td>
                </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
